import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { User } from '@cl/shared/models/users/user.model';
import { BaseServiceWithFilter } from './base.service';
import { UsersDepartment } from '@cl/shared/models/users/users-department.model';
import { lastValueFrom, Observable } from 'rxjs';
import { CommandResult } from '@cl/shared/models';
import { UserFilter } from '@cl/shared/models/users/user-filter.model';

@Injectable({ providedIn: 'root' })
export class UsersService extends BaseServiceWithFilter<User, UserFilter> {
    constructor(protected httpClient: HttpClient) {
        super(httpClient, '/api/v1/users/');
    }

    delete(id: number): Promise<void> {
        throw new Error('Not allowed');
    }

    getPermissions(userId: number): Promise<UsersDepartment[]> {
        return this.httpClient.get<UsersDepartment[]>(`${this.baseApiUrl}${userId}/permissions`).toPromise();
    }

    setPermission(userId: number, departmentId: number, permissionId: number): Promise<number> {
        return this.httpClient.post<number>(`${this.baseApiUrl}${userId}/departments/${departmentId}/permissions/${permissionId}`, null).toPromise();
    }

    removePermission(userId: number, departmentId: number, permissionId: number): Promise<void> {
        return this.httpClient.delete<void>(`${this.baseApiUrl}${userId}/departments/${departmentId}/permissions/${permissionId}`).toPromise();
    }

    async resetTotp(userId: number): Promise<CommandResult<void>> {
        return await lastValueFrom(this.httpClient.patch<CommandResult<void>>(`${this.baseApiUrl}${userId}/reset-totp`, null));
    }

    searchBetweenUserColleagues(searchText: string): Observable<CommandResult<User[]>> {
        return this.httpClient.get<CommandResult<User[]>>(`${this.baseApiUrl}search-colleagues/${searchText}`);
    }

    getCompanyUsers(companyId: number): Promise<CommandResult<User[]>> {
        return lastValueFrom(this.httpClient.get<CommandResult<User[]>>(`${this.baseApiUrl}company-users/${companyId}`));
    }

    isPersonCodeUnique(code: string, id: number): Observable<boolean> {
        return this.httpClient.get<boolean>(`${this.baseApiUrl}${code}/unique/${id}`);
    }

    isUsernameUnique(username: string, id: number): Observable<boolean> {
        return this.httpClient.get<boolean>(`${this.baseApiUrl}${username}/username-unique/${id}`);
    }

    getUsersForDocumentSigning(documentId: number): Promise<CommandResult<User[]>> {
        return lastValueFrom(this.httpClient.get<CommandResult<User[]>>(`${this.baseApiUrl}for-document-signing/${documentId}`));
    }

    searchUsersWithDepartmentPermission(departmentId: number, permissionId: number, searchText: string): Observable<CommandResult<User[]>> {
        return this.httpClient.get<CommandResult<User[]>>(
            `${this.baseApiUrl}search-with-department-permission/${departmentId}/${permissionId}/${searchText}`,
        );
    }

    getUsersWithDepartmentPermission(departmentId: number, permissionId: number): Observable<CommandResult<User[]>> {
        return this.httpClient.get<CommandResult<User[]>>(`${this.baseApiUrl}with-department-permission/${departmentId}/${permissionId}`);
    }

    getGroupUsers(groupId: number): Observable<CommandResult<User[]>> {
        return this.httpClient.get<CommandResult<User[]>>(`${this.baseApiUrl}group-users/${groupId}`);
    }

    getUsersByQueryParams(userQueryParams: HttpParams): Observable<CommandResult<User[]>> {
        return this.httpClient.get<CommandResult<User[]>>(`${this.baseApiUrl}get-by-query`, { params: userQueryParams });
    }

    async logoutUserById(userId: number): Promise<CommandResult<void>> {
        return lastValueFrom(this.httpClient.post<CommandResult<void>>(`${this.baseApiUrl}${userId}/logout`, null));
    }

    async getAllRelatedUserIds(): Promise<CommandResult<number[]>> {
        return lastValueFrom(this.httpClient.get<CommandResult<number[]>>(`${this.baseApiUrl}all-related-ids`));
    }

    async resendUserRegistrationInvitation(userId: number): Promise<CommandResult<void>> {
        return lastValueFrom(this.httpClient.put<CommandResult<void>>(`${this.baseApiUrl}resend-invitation/${userId}`, null));
    }

    async getPerformerUser(performerId: number): Promise<CommandResult<User>> {
        return lastValueFrom(this.httpClient.get<CommandResult<User>>(`${this.baseApiUrl}performer-user/${performerId}`));
    }

    getUsersAccessibleByDepartmentPermission(departmentPermissionId: number): Observable<CommandResult<User[]>> {
        return this.httpClient.get<CommandResult<User[]>>(`${this.baseApiUrl}accessible-by-department-permission/${departmentPermissionId}`);
    }

    toString(object: User): string {
        return `${object?.name || ''} ${object?.surname || ''} ${object?.userEmail ? ['(', object.userEmail, ')'].join('') : ''}`;
    }
}
